import { matchesTeams, searchTokens } from "../../src/lib/results/team-search";

/**
 * Luetteloiden suodatus (`[data-filter-scope]`): tasoryhmien näytä/piilota-valitsimet
 * (`form[data-toggle-filter]`, Joukkueet ja Ottelut) ja joukkuehaku (`input[data-filter-search]`, Ottelut).
 * Ilman JavaScriptia lomakkeet lähetetään ja palvelin suodattaa; tässä sama tehdään sivua lataamatta:
 *  - rivi `[data-filter-key]` näkyy, kun sen tasoryhmä on valittu ja (jos rivillä on `data-filter-teams`)
 *    jonkin joukkueen nimessä on haun kaikki sanat (src/lib/results/team-search.ts);
 *  - ryhmät `[data-filter-group]` (kortit, päivät) piiloutuvat, kun niissä ei ole näkyviä rivejä;
 *  - tyhjät tilat: `[data-filter-empty]` kun kaikki tasoryhmät on piilotettu, `[data-filter-nomatch]` kun haku
 *    ei löydä mitään; `[data-filter-list]` piiloutuu, kun mitään ei näy;
 *  - osoite (`?piilota=…&haku=…`, korvaa `?tasoryhma=…`) päivitetään, jotta valinta säilyy sivun päivityksessä ja
 *    jaetussa linkissä, ja sama valinta viedään linkkeihin ja lomakkeisiin `[data-filter-link]` (välilehdet).
 */
export function setupListFilters(): void {
  for (const scope of document.querySelectorAll<HTMLElement>("[data-filter-scope]")) {
    const toggles = scope.querySelector<HTMLFormElement>("form[data-toggle-filter]");
    const search = scope.querySelector<HTMLInputElement>("input[data-filter-search]");
    if (!toggles && !search) continue;
    const buttons = toggles ? [...toggles.querySelectorAll<HTMLButtonElement>("button[aria-pressed]")] : [];
    const rows = [...scope.querySelectorAll<HTMLElement>("[data-filter-key]")];
    const groups = [...scope.querySelectorAll<HTMLElement>("[data-filter-group]")];
    const list = scope.querySelector<HTMLElement>("[data-filter-list]");
    const empty = scope.querySelector<HTMLElement>("[data-filter-empty]");
    const noMatch = scope.querySelector<HTMLElement>("[data-filter-nomatch]");

    const apply = () => {
      const hidden = buttons.filter((b) => b.getAttribute("aria-pressed") !== "true").map((b) => b.value);
      const query = search?.value.trim() ?? "";
      const tokens = searchTokens(query);
      let inDivisions = 0;
      let visible = 0;
      for (const row of rows) {
        const divisionShown = !hidden.includes(row.dataset.filterKey ?? "");
        const teams = row.dataset.filterTeams;
        row.hidden = !divisionShown || (teams !== undefined && !matchesTeams(teams.split("\n"), tokens));
        if (divisionShown) inDivisions++;
        if (!row.hidden) visible++;
      }
      for (const group of groups) group.hidden = !group.querySelector("[data-filter-key]:not([hidden])");
      if (list) list.hidden = visible === 0;
      if (empty) empty.hidden = inDivisions > 0;
      if (noMatch) noMatch.hidden = inDivisions === 0 || visible > 0;

      const setParams = (params: URLSearchParams) => {
        params.delete("piilota");
        for (const id of hidden) params.append("piilota", id);
        if (search) {
          if (query) params.set("haku", query);
          else params.delete("haku");
        }
      };
      const url = new URL(window.location.href);
      url.searchParams.delete("vaihda");
      url.searchParams.delete("tasoryhma");
      setParams(url.searchParams);
      window.history.replaceState(window.history.state, "", url);
      for (const link of document.querySelectorAll<HTMLAnchorElement>("a[data-filter-link]")) {
        const target = new URL(link.href, window.location.href);
        setParams(target.searchParams);
        link.href = `${target.pathname}${target.search}${target.hash}`;
      }
      for (const form of document.querySelectorAll<HTMLFormElement>("form[data-filter-link]")) {
        for (const input of form.querySelectorAll('input[type="hidden"][name="piilota"]')) input.remove();
        for (const id of hidden) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = "piilota";
          input.value = id;
          form.insertBefore(input, form.firstChild);
        }
      }
    };

    toggles?.addEventListener("click", (ev) => {
      const button = (ev.target as Element | null)?.closest<HTMLButtonElement>("button[aria-pressed]");
      if (!button || !toggles.contains(button)) return;
      ev.preventDefault();
      button.setAttribute("aria-pressed", button.getAttribute("aria-pressed") === "true" ? "false" : "true");
      apply();
    });
    if (search) {
      // Suodatus tapahtuu kirjoitettaessa, joten hakunappia ja lomakkeen lähetystä ei tarvita.
      search.addEventListener("input", apply);
      search.form?.addEventListener("submit", (ev) => ev.preventDefault());
      for (const button of scope.querySelectorAll<HTMLElement>("[data-filter-submit]")) button.hidden = true;
      // live.js voi vaihtaa sisällön kesken kirjoittamisen ja siirtää uudemman hakusanan uuteen kenttään.
      if (search.value !== search.defaultValue) apply();
    }
  }
}
